import { useEffect, useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'

import Banner from 'common/components/banner'
import Button from 'common/components/button'
import Checkbox from 'common/components/checkbox'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import Modal from 'common/components/Modal'
import { Close, MissingDataInverted } from 'common/icons'

const MAX_WORKFLOW_NAME_LENGTH = 236
const MAX_HUMAN_CONTROL_LENGTH = 128

export type DuplicateWorkflowModalElement = {
  elementId: number
  elementType?: 'component' | 'template' | string
  templateName?: string
  projectName?: string
  ownSubjectLine?: string
}

export type HumanControlLineByElement = {
  elementId: number
  text: string
}

export type DuplicateWorkflowModalValues = {
  name: string
  manuallyEnterName: boolean
  includeTemplateAndBrief: boolean
  includeGeneratedContent: boolean
  editHumanControlLine: boolean
  humanControlLines: HumanControlLineByElement[]
}

type Props = {
  isOpen: boolean
  initialName: string
  elements: DuplicateWorkflowModalElement[]
  isLoading?: boolean
  isPersonaliseCampaign?: boolean
  onClose: () => void
  onConfirm: (values: DuplicateWorkflowModalValues) => Promise<void> | void
}

const DuplicateWorkflowModal = ({
  isOpen,
  initialName,
  elements,
  isLoading,
  isPersonaliseCampaign = false,
  onClose,
  onConfirm,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showMultipleTemplatesBanner, setShowMultipleTemplatesBanner] =
    useState(true)

  const handleSubmit = async (values: DuplicateWorkflowModalValues) => {
    setIsSubmitting(true)
    try {
      await onConfirm({
        ...values,
        // Personalise campaigns must never duplicate generated/optimised variants.
        includeGeneratedContent: isPersonaliseCampaign
          ? false
          : values.includeGeneratedContent ?? false,
      })
      onClose()
    } catch (error) {
      return { name: 'Failed to duplicate workflow' }
    } finally {
      setIsSubmitting(false)
    }
  }

  const loading = isSubmitting || !!isLoading
  const controllableElements = useMemo(
    () =>
      elements.filter((e) => (e.elementType ?? 'component') === 'component'),
    [elements]
  )
  const hasMultipleElements = controllableElements.length > 1
  const hasAnyControllableElements = controllableElements.length > 0

  const initialHumanControlLines = useMemo<HumanControlLineByElement[]>(
    () =>
      controllableElements.map((e) => ({
        elementId: e.elementId,
        text: e.ownSubjectLine ?? '',
      })),
    [controllableElements]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setShowMultipleTemplatesBanner(true)
  }, [isOpen, controllableElements.length])

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      visible={true}
      centered
      closable={true}
      footer={null}
      onClose={onClose}
      data-cy="duplicate-workflow-modal"
      data-testid="duplicate-workflow-modal"
      style={{ minWidth: 560 }}
    >
      <div className="flex w-full">
        <h1 className="mt-2 text-2xl font-bold text-coolGray-800">
          Make a duplicate
        </h1>
      </div>

      <div className="mt-4 text-base text-coolGray-600">
        <Form<DuplicateWorkflowModalValues>
          onSubmit={handleSubmit}
          initialValues={{
            name: initialName,
            manuallyEnterName: false,
            includeTemplateAndBrief: true,
            includeGeneratedContent: false,
            editHumanControlLine: false,
            humanControlLines: initialHumanControlLines,
          }}
          validate={(values) => {
            const errors: Partial<
              Record<keyof DuplicateWorkflowModalValues, string>
            > = {}

            if (values.manuallyEnterName) {
              if (!values.name?.trim()) {
                errors.name = 'Name is required'
              }
              if (values.name?.length > MAX_WORKFLOW_NAME_LENGTH) {
                errors.name = `Name should be up to ${MAX_WORKFLOW_NAME_LENGTH} characters`
              }
            }

            const anyTooLong = values.humanControlLines?.some(
              (l) => (l?.text ?? '').length > MAX_HUMAN_CONTROL_LENGTH
            )
            if (anyTooLong) {
              errors.humanControlLines = `Control line should be up to ${MAX_HUMAN_CONTROL_LENGTH} characters`
            }

            return errors
          }}
          render={({ handleSubmit, hasValidationErrors, values }) => (
            <form onSubmit={handleSubmit} className="text-sm min-w-200">
              <div className="space-y-2">
                <Field<boolean> name="manuallyEnterName">
                  {({ input }) => (
                    <Checkbox
                      isChecked={Boolean(input.value)}
                      onChange={(checked) => input.onChange(checked)}
                      isDisabled={loading}
                      label="Use a custom name (optional)"
                    />
                  )}
                </Field>

                <p className="text-xs text-coolGray-500">
                  Leave this unchecked to generate a unique name automatically.
                </p>

                {values.manuallyEnterName && (
                  <Field<string>
                    name="name"
                    render={({ input, meta }) => (
                      <FormItem
                        label="Workflow name"
                        htmlFor="duplicate_workflow_name"
                        error={
                          meta.error ||
                          (!meta.dirtySinceLastSubmit && meta.submitError) ||
                          undefined
                        }
                      >
                        <div className="relative">
                          <div className="absolute right-0 -top-6 text-coolGray-500 text-xs font-normal">
                            {input.value?.length || 0} /{' '}
                            {MAX_WORKFLOW_NAME_LENGTH}
                          </div>
                          <Input
                            type="text"
                            id="duplicate_workflow_name"
                            variant="default"
                            className="text-coolGray-800"
                            value={input.value}
                            name={input.name}
                            onChange={input.onChange}
                            disabled={loading}
                            maxLength={MAX_WORKFLOW_NAME_LENGTH}
                          />
                        </div>
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="mt-2 space-y-2">
                <Field<boolean> name="includeTemplateAndBrief">
                  {({ input }) => (
                    <Checkbox
                      isChecked={true}
                      onChange={(checked) => input.onChange(checked)}
                      isDisabled={true}
                      label="Including template and brief"
                    />
                  )}
                </Field>

                {!isPersonaliseCampaign && (
                  <Field<boolean> name="includeGeneratedContent">
                    {({ input }) => (
                      <Checkbox
                        isChecked={Boolean(input.value)}
                        onChange={(checked) => input.onChange(checked)}
                        isDisabled={loading}
                        label="Including generated content"
                      />
                    )}
                  </Field>
                )}

                {hasAnyControllableElements && (
                  <Field<boolean> name="editHumanControlLine">
                    {({ input }) => (
                      <Checkbox
                        isChecked={Boolean(input.value)}
                        onChange={(checked) => input.onChange(checked)}
                        isDisabled={loading}
                        label="Edit the Human Control line"
                      />
                    )}
                  </Field>
                )}
              </div>

              {hasAnyControllableElements && values.editHumanControlLine && (
                <div className="mt-6 space-y-6">
                  {hasMultipleElements && showMultipleTemplatesBanner && (
                    <Banner
                      type="info"
                      icon={
                        <MissingDataInverted
                          className="text-base-700"
                          isDefaultColor={false}
                        />
                      }
                      heading="MULTIPLE TEMPLATES DETECTED"
                      text={`This workflow contains ${controllableElements.length} templates with Human Control. Review each control line below duplicating.`}
                      cta={
                        <button
                          type="button"
                          onClick={() => setShowMultipleTemplatesBanner(false)}
                          className="text-base-700 hover:text-base-900"
                          aria-label="Dismiss multiple templates banner"
                        >
                          <Close isDefaultColor={false} size={4} />
                        </button>
                      }
                    />
                  )}

                  {controllableElements.map((element, index) => (
                    <div
                      key={element.elementId}
                      className="border-t border-gold-200 pt-4"
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold tracking-wide text-coolGray-800">
                        SUBJECT LINE
                        {element.templateName ? (
                          <span className="font-normal text-coolGray-600">
                            ({element.templateName})
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-6">
                        <div>
                          <div className="text-xs font-semibold text-coolGray-700">
                            Project
                          </div>
                          <div className="mt-2 rounded border border-gold-200 bg-gold-50 px-3 py-2 text-sm text-coolGray-700">
                            {element.projectName ?? '-'}
                          </div>
                        </div>

                        <Field<string>
                          name={`humanControlLines[${index}].text`}
                        >
                          {({ input }) => (
                            <FormItem
                              label="Control subject line"
                              htmlFor={`duplicate_workflow_human_control_${element.elementId}`}
                            >
                              <div className="relative">
                                <div className="absolute right-0 -top-6 text-coolGray-500 text-xs font-normal">
                                  {input.value?.length || 0} /{' '}
                                  {MAX_HUMAN_CONTROL_LENGTH}
                                </div>
                                <Input
                                  type="text"
                                  id={`duplicate_workflow_human_control_${element.elementId}`}
                                  variant="default"
                                  className="text-coolGray-800"
                                  value={input.value}
                                  name={input.name}
                                  onChange={input.onChange}
                                  disabled={loading}
                                  maxLength={MAX_HUMAN_CONTROL_LENGTH}
                                />
                                <p className="mt-1 text-xs text-coolGray-500">
                                  Keep the same or enter a new control line.
                                </p>
                              </div>
                            </FormItem>
                          )}
                        </Field>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-8">
                <Button
                  variant="link"
                  className="mr-4 text-base text-coolGray-400"
                  onClick={onClose}
                  data-cy="duplicate-workflow-modal-cancel-button"
                  data-testid="duplicate-workflow-modal-cancel-button"
                  disabled={loading}
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={
                    loading ||
                    hasValidationErrors ||
                    (values.manuallyEnterName && !values.name?.trim())
                  }
                  data-cy="duplicate-workflow-modal-confirm-button"
                  data-testid="duplicate-workflow-modal-confirm-button"
                >
                  DUPLICATE
                </Button>
              </div>
            </form>
          )}
        />
      </div>
    </Modal>
  )
}

export default DuplicateWorkflowModal
